import { type ReactNode } from 'react';
import { Smartphone, Tablet } from 'lucide-react';
import { useDevicePreviewStore, type PresetKey } from '@/lib/device-preview-store';

/**
 * DevicePreview — development-time viewport simulator.
 *
 * Wraps the entire app in a fixed-size frame so you can see the UI at
 * iPad / iPhone / tablet dimensions without connecting a real device.
 * The frame includes a thin bezel, corner radius, and a status label
 * showing the simulated resolution so it's obvious what you're
 * looking at.
 *
 * Persists the selected preset + orientation to localStorage so the
 * view survives reloads. Default is OFF (desktop/native window).
 *
 * Toggle is a floating pill bottom-right. Click to open the preset
 * picker, click a preset to enter that frame, click the active
 * preset again to exit.
 *
 * This is not limited to dev mode — it's also useful during demos and
 * screenshot sessions, so it ships in production too. The control
 * takes up very little space when idle.
 */

interface Preset {
  key: PresetKey;
  label: string;
  short: string;
  kind: 'tablet' | 'phone' | 'off';
  /** Logical CSS pixels, portrait. Landscape is w/h swapped. */
  portrait: { w: number; h: number };
}

export const PRESETS: Preset[] = [
  { key: 'off',                label: 'Native / Off',      short: 'OFF',    kind: 'off',    portrait: { w: 0, h: 0 } },
  { key: 'ipad-10',            label: 'iPad (10th gen)',   short: 'iPad',   kind: 'tablet', portrait: { w: 820, h: 1180 } },
  { key: 'ipad-pro-11',        label: 'iPad Pro 11"',      short: 'Pro 11', kind: 'tablet', portrait: { w: 834, h: 1194 } },
  { key: 'ipad-pro-13',        label: 'iPad Pro 13"',      short: 'Pro 13', kind: 'tablet', portrait: { w: 1024, h: 1366 } },
  { key: 'ipad-mini',          label: 'iPad mini',         short: 'Mini',   kind: 'tablet', portrait: { w: 744, h: 1133 } },
  { key: 'iphone-15',          label: 'iPhone 15',         short: '15',     kind: 'phone',  portrait: { w: 393, h: 852 } },
  { key: 'iphone-15-pro-max',  label: 'iPhone 15 Pro Max', short: '15 PM',  kind: 'phone',  portrait: { w: 430, h: 932 } },
];

interface Props {
  children: ReactNode;
}

export function DevicePreview({ children }: Props) {
  const preset_key = useDevicePreviewStore((s) => s.preset);
  const orientation = useDevicePreviewStore((s) => s.orientation);

  const preset = PRESETS.find((p) => p.key === preset_key) ?? PRESETS[0];
  const on = preset.kind !== 'off';

  const viewport = on
    ? (orientation === 'portrait'
        ? preset.portrait
        : { w: preset.portrait.h, h: preset.portrait.w })
    : null;

  return (
    <>
      {/* The frame — only rendered when a preset is active */}
      {viewport ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center overflow-auto p-6"
          style={{
            // Checkerboard backdrop so the device frame is obvious
            backgroundColor: 'rgb(15 23 42)',
            backgroundImage:
              'linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%), ' +
              'linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%)',
            backgroundSize: '24px 24px',
            backgroundPosition: '0 0, 12px 12px',
          }}
        >
          <div className="flex flex-col items-center gap-2">
            {/* Device label */}
            <div className="flex items-center gap-2 rounded-none border border-slate-700 bg-slate-900 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
              {preset.kind === 'tablet' ? <Tablet size={11} /> : <Smartphone size={11} />}
              <span className="text-slate-200 font-semibold">{preset.label}</span>
              <span className="text-slate-500">·</span>
              <span>{viewport.w} × {viewport.h}</span>
              <span className="text-slate-500">·</span>
              <span>{orientation}</span>
            </div>

            {/* Device frame */}
            <div
              className="shadow-2xl ring-1 ring-black/60"
              style={{
                width: viewport.w + 16,
                height: viewport.h + 16,
                padding: 8,
                backgroundColor: 'rgb(24 24 27)', // zinc-900 bezel
                borderRadius: preset.kind === 'phone' ? 36 : 24,
                maxHeight: 'calc(100vh - 80px)',
                maxWidth: 'calc(100vw - 48px)',
              }}
            >
              <div
                className="h-full w-full overflow-auto bg-slate-950"
                style={{
                  borderRadius: preset.kind === 'phone' ? 30 : 16,
                }}
              >
                {/* The simulated viewport. width/height are set via CSS so
                    CSS media queries fire off the container instead of the
                    real window when components use Tailwind responsive
                    prefixes. Note: this is a visual aid only — Tailwind's
                    md: / lg: breakpoints still read the real window width.
                    For accurate responsive testing, also resize the browser
                    window. */}
                <div
                  style={{
                    width: viewport.w,
                    height: viewport.h,
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                >
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // When OFF, render children inline (no frame)
        children
      )}
    </>
  );
}
